import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/app/_module/lib/utils';

export type StepState = 'done' | 'current' | 'action' | 'upcoming';

export interface ReviewStep {
  key: string;
  title: string;
  detail?: React.ReactNode;
  state: StepState;
}

// Every step states itself in words; the marker only repeats it.
const STATE: Record<StepState, { label: string; text: string }> = {
  done: { label: 'Done', text: 'text-emerald-700' },
  current: { label: 'In progress', text: 'text-amber-700' },
  action: { label: 'Your turn', text: 'text-amber-700 font-semibold' },
  upcoming: { label: 'Next', text: 'text-neutral-500' },
};

function Marker({ state }: { state: StepState }) {
  if (state === 'done') {
    return (
      <span className="flex size-3.5 items-center justify-center rounded-full bg-emerald-600">
        <Check className="size-2.5 text-white" strokeWidth={3.5} />
      </span>
    );
  }
  if (state === 'upcoming') {
    return <span className="size-2.5 rounded-full border-[1.5px] border-neutral-400 bg-white" />;
  }
  return <span className="size-2.5 rounded-full bg-amber-600 ring-4 ring-amber-500/20" />;
}

export function ReviewSteps({ steps }: { steps: ReviewStep[] }) {
  return (
    <ol>
      {steps.map((step, i) => {
        const last = i === steps.length - 1;
        return (
          <li key={step.key} className={cn('relative flex gap-4', !last && 'pb-6')}>
            {!last && (
              <span
                aria-hidden
                className={cn(
                  'absolute bottom-px left-[6.5px] top-[21px] w-px',
                  step.state === 'done' ? 'bg-emerald-600/35' : 'bg-neutral-200',
                )}
              />
            )}
            <span aria-hidden className="mt-[3px] flex size-3.5 shrink-0 items-center justify-center">
              <Marker state={step.state} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <p className={cn('text-sm font-medium', step.state === 'upcoming' ? 'text-neutral-500' : 'text-neutral-900')}>
                  {step.title}
                </p>
                <span className={cn('text-xs', STATE[step.state].text)}>{STATE[step.state].label}</span>
              </div>
              {step.detail && <p className="mt-1 text-pretty text-sm leading-relaxed text-neutral-500">{step.detail}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
